import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InputLabel from "@mui/material/InputLabel";
import {
  getCardById,
  getCardByNameOrId,
  saveLeadsFormByCardId,
} from "../../../services/apiService";
import { Dictionary } from "lodash";
import {
  CardFieldNames,
  DigitalCard,
} from "../../../services/apiService/responseModels";
import { GetServerSideProps, NextPage } from "next";
import cdnService from "../../../services/cdnService";
import {
  Grid,
  useTheme,
  Card as MCard,
  Box,
  alpha,
  CardMedia,
  Avatar,
  styled,
  Typography,
  Button,
  darken,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import * as useSnackbar from "notistack";
import { TextField } from "formik-mui";
import ReviewFooter from "../../../components/review-footer";
import { getLeadSettingsByClientId } from "../../../services/apiService/nestJsApi";
import { LeadSettings } from "../../../services/apiService/responseModels/nestJsApisResonseModel";

const rePhoneNumber =
  /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

type ViewerProps = {
  cardName: string;
  hasError: boolean;
  card: DigitalCard | null;
  categories: string[];
};

const CardActions = styled(Box)(
  ({ theme }) => `
    position: absolute;
    right: ${theme.spacing(2)};
    bottom: ${theme.spacing(2)};
    z-index: 7;
  `
);

const BgComposed = styled(Box)(
  ({ theme }) => `
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    border-radius: inherit;
    transition: ${theme.transitions.create(["opacity"])};
    background: ${darken(alpha(theme.colors.primary.main, 0.9), 0.8)};
    z-index: 6;
    opacity: 0;
    box-shadow: inset 0 0 2.3rem 0.5rem ${darken(
      theme.colors.primary.main,
      0.9
    )};
  `
);

const IconButtonWrapper = styled(IconButton)(
  ({ theme }) => `
        width: ${theme.spacing(6)};
        height: ${theme.spacing(6)};
        transition: ${theme.transitions.create(["color", "transform"])};
        border-radius: 100px;

        .MuiSvgIcon-root {
            transform: scale(1);
            transition: ${theme.transitions.create(["transform"])};
        }

        &:hover {
            .MuiSvgIcon-root {
                transform: scale(1.3);
            }
        }
  `
);
const LeadForm: NextPage<ViewerProps> = (props) => {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar.useSnackbar();
  const cardId: number = props?.card?.id || 0;
  const fieldHash = useMemo(() => {
    const fieldHash: Dictionary<String> = {};
    (props.card?.fields || []).forEach(
      (o) =>
        (fieldHash[o.fieldName] =
          o.formFieldValues.find(({ name }) => name === o.fieldName)?.value ||
          (o.formFieldValues.length ? o.formFieldValues[0].value : ""))
    );
    return fieldHash;
  }, [props.card?.fields]);

  const [saving, setSaving] = useState(false);

  const theme = useTheme();
  const getImageUrl = () => {
    let imageUrl = "";
    const values = props.card?.fields.find(
      (o) => o.fieldName == CardFieldNames.ProfilePic
    )?.formFieldValues;
    if (values) {
      imageUrl =
        values.find((o) => o.name == CardFieldNames.ProfilePic)?.value ||
        "no-image.png";
      imageUrl = cdnService.toImageUrl(imageUrl);
    }
    return imageUrl;
  };
  return (
    <>
      <Grid container>
        <Grid item xs={1} md={4}></Grid>
        <Grid item xs={10} sx={{ mt: 5 }} md={4}>
          <MCard
            sx={{
              textAlign: "center",
              transition: `${theme.transitions.create([
                "box-shadow",
                "transform",
              ])}`,
              transform: "translateY(0px)",

              "&:hover": {
                transform: "translateY(-10px)",
                boxShadow: `0 2rem 8rem 0 ${alpha(
                  theme.colors.alpha.black[100],
                  0.05
                )}, 
                0 0.6rem 1.6rem ${alpha(theme.colors.alpha.black[100], 0.15)}, 
                0 0.2rem 0.2rem ${alpha(theme.colors.alpha.black[100], 0.1)}`,
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                transition: `${theme.transitions.create([
                  "box-shadow",
                  "transform",
                  "border-radius",
                ])}`,
                transform: "translateY(0px)",
                borderRadius: "inherit",
              }}
            >
              <BgComposed
                display="flex"
                alignItems="center"
                justifyContent="center"
                className="MuiBgComposed"
              >
                <IconButtonWrapper
                  sx={{
                    mx: 0.5,
                    background: `${theme.colors.primary.main}`,
                    color: `${theme.palette.getContrastText(
                      theme.colors.primary.main
                    )}`,

                    "&:hover": {
                      background: `${theme.colors.primary.main}`,
                      color: `${theme.palette.getContrastText(
                        theme.colors.primary.main
                      )}`,
                    },
                  }}
                >
                  <FacebookIcon fontSize="small" />
                </IconButtonWrapper>
                <IconButtonWrapper
                  sx={{
                    mx: 0.5,
                    background: `${theme.colors.primary.main}`,
                    color: `${theme.palette.getContrastText(
                      theme.colors.primary.main
                    )}`,

                    "&:hover": {
                      background: `${theme.colors.primary.main}`,
                      color: `${theme.palette.getContrastText(
                        theme.colors.primary.main
                      )}`,
                    },
                  }}
                >
                  <TwitterIcon fontSize="small" />
                </IconButtonWrapper>
              </BgComposed>
              {fieldHash[CardFieldNames.CoverPhoto] ? (
                <CardMedia
                  component="img"
                  sx={{
                    borderRadius: "inherit",
                    position: "relative",
                    zIndex: 5,
                  }}
                  image={cdnService.toImageUrl(
                    fieldHash[CardFieldNames.CoverPhoto] as string
                  )}
                  alt="..."
                />
              ) : (
                <>
                  <CardMedia
                    sx={{
                      borderRadius: "inherit",
                      position: "relative",
                      zIndex: 5,
                    }}
                    component="img"
                    height="130"
                    alt="..."
                    src="/assets/img/review-bg1.webp"
                  />
                  <CardActions></CardActions>
                </>
              )}
            </Box>
            <Box p={4}>
              <Avatar
                variant="rounded"
                sx={{
                  width: `${theme.spacing(14)}`,
                  height: `${theme.spacing(14)}`,
                  mt: `-${theme.spacing(10)}`,
                  mx: "auto",
                  boxShadow: `0 0 0 4px ${theme.colors.alpha.white[100]}`,
                }}
                src={getImageUrl()}
              />
              <Typography
                sx={{
                  py: 2,
                }}
                variant="h3"
              >
                {fieldHash[CardFieldNames.BusinessName]}
              </Typography>
            </Box>
            <Formik
              enableReinitialize={true}
              validationSchema={Yup.object().shape({
                contactName: Yup.string()
                  .max(250)
                  .required("The field is required"),
                contactEmail: Yup.string()
                  .max(250)
                  .required("The field is required")
                  .email("Not a proper email"),
                contactMobile: Yup.string()
                  .required("The field is required")
                  .matches(rePhoneNumber, "Phone number is not valid"),
                ...(props.categories.length && {
                  leadCategory: Yup.string().required("The field is required"),
                }),
              })}
              onSubmit={async (
                _values,
                { resetForm, setErrors, setStatus, setSubmitting }
              ) => {
                try {
                  setSaving(true);
                  const {
                    contactName,
                    contactEmail,
                    contactMobile,
                    remark,
                    leadCategory,
                  } = _values;

                  const leadFormModel = {
                    cardId,
                    contactName,
                    contactEmail,
                    contactMobile,
                    remark,
                    leadCategory,
                  };
                  await saveLeadsFormByCardId(leadFormModel);
                  enqueueSnackbar("Form submitted!!", { variant: "success" });
                  setSaving(false);
                  resetForm();
                  setSubmitting(false);
                  router.push("/app/lead-form/success");
                } catch (error) {
                  console.error(error);
                  enqueueSnackbar("Failed to submit form", {
                    variant: "error",
                  });

                  setSaving(false);
                  setSubmitting(false);
                }
              }}
              initialValues={{
                contactName: "",
                contactEmail: "",
                contactMobile: "",
                remark: "",
                leadCategory: "",
              }}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                isValid,
                errors,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form noValidate onSubmit={handleSubmit}>
                  {props.categories.length > 0 && (
                    <Grid sx={{ padding: theme.spacing(2) }}>
                      <FormControl
                        error={Boolean(
                          touched.leadCategory && errors.leadCategory
                        )}
                        fullWidth
                      >
                        <InputLabel id="demo-simple-select-error-label">
                          Category
                        </InputLabel>
                        <Select
                          name="leadCategory"
                          value={values.leadCategory}
                          onChange={(e) => {
                            setFieldValue("leadCategory", e.target.value);
                          }}
                          placeholder="Select lead category"
                        >
                          {props.categories.map((category: string) => (
                            <MenuItem key={category} value={category}>
                              {category}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {touched.leadCategory && errors.leadCategory}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid sx={{ padding: theme.spacing(2) }}>
                    <Field
                      fullWidth
                      rows={3}
                      name="remark"
                      multiline
                      component={TextField}
                      label="Enter your query (optional)"
                    />
                  </Grid>
                  <Grid sx={{ padding: theme.spacing(2) }}>
                    <Field
                      fullWidth
                      name="contactName"
                      value={values.contactName}
                      component={TextField}
                      label="Name*"
                      placeholder="Enter your name"
                    />
                  </Grid>
                  <Grid sx={{ padding: theme.spacing(2) }}>
                    <Field
                      fullWidth
                      name="contactEmail"
                      value={values.contactEmail}
                      component={TextField}
                      label="Email*"
                      placeholder="Enter your email"
                    />
                  </Grid>
                  <Grid sx={{ padding: theme.spacing(2) }}>
                    <Field
                      fullWidth
                      name="contactMobile"
                      value={values.contactMobile}
                      component={TextField}
                      label="Contact Number*"
                      placeholder="Enter your contact number"
                    />
                  </Grid>
                  <Grid sx={{ padding: theme.spacing(2) }}>
                    <Button disabled={isSubmitting || saving} type="submit">
                      {saving ? "Saving..." : "Submit form"}
                    </Button>
                  </Grid>
                </Form>
              )}
            </Formik>
          </MCard>
          <ReviewFooter
            email={fieldHash[CardFieldNames.Email] as string}
            contactNumber={fieldHash[CardFieldNames.ContactNumber] as string}
            website={fieldHash[CardFieldNames.Website] as string}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default LeadForm;

export const getServerSideProps: GetServerSideProps = async (context) => {
  const cardName = (context.query.cardId as string) || "0";

  let hasError = false;
  let leadSettings: LeadSettings | null = null;
  let card: DigitalCard | null = null;
  try {
    const response = await getCardByNameOrId(cardName);
    card = response.data;
    if (card?.clientAccountId) {
      const result = await getLeadSettingsByClientId(card.clientAccountId);
      leadSettings = result.data;
    }
  } catch (ex) {
    console.log(ex);
    hasError = true;
  }

  const result: ViewerProps = {
    cardName: cardName,
    hasError: hasError,
    card,
    categories: leadSettings?.leadCategories || [],
  };
  return {
    props: result,
  };
};
